import { useMemo } from 'react';

import useSongIndex from '~/modules/songs/hooks/use-song-index';
import useSongTags from '~/modules/songs/hooks/use-song-tags';
import { mergeSongTag } from '~/modules/songs/utils/song-tags';

/**
 * The song index with what we know beyond the song files merged onto it.
 *
 * Merged onto the songs rather than threaded through the filters, so anything that reads a song can
 * see it - the search does today, and a "more from this series" control would tomorrow. Shared
 * rather than done where the list is built, because the TV and the phone read the list in different
 * places and a song's series should not depend on which screen is asking.
 */
export default function useTaggedSongIndex() {
  // React Compiler: inherits `useSongIndex`'s opt-out. That hook reads mutable external state at
  // render and says so; wrapping it hands the same problem to whatever wraps it, and here the
  // symptom was a restored song staying gone - the list kept serving the index from before it came
  // back.
  'use no memo';
  const songList = useSongIndex();
  const { songTags } = useSongTags();

  /*
   * Keyed off `songList.data`, which is memoised, and not off the hook's return value, which is a
   * fresh object every render - depending on that rebuilt this array constantly, and with it every
   * hook downstream that keys off the list.
   */
  const data = useMemo(
    () => songList.data.map((song) => mergeSongTag(song, songTags[song.id])),
    [songList.data, songTags],
  );

  return { data, isLoading: songList.isLoading, reload: songList.reload };
}
